import React, { useState } from "react";
import { useLoginMutation } from "../../services/queries/auth";
import logo from "../../assets/logo.svg";
import { setCredentials } from "../../features/auth/authSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    email: "",
    password: "",
    general: "",
  });

  const [touched, setTouched] = useState({
    email: false,
    password: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loginMutation = useLoginMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const validateEmail = (email) => {
    if (!email.trim()) {
      return "Email is required";
    }
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePassword = (password) => {
    if (!password) {
      return "Password is required";
    }
    if (password.length < 6) {
      return "Password must be at least 6 characters long";
    }
    if (!/[a-zA-Z]/.test(password)) {
      return "Password must contain at least one letter";
    }
    return "";
  };

  const validateField = (name, value) => {
    switch (name) {
      case "email":
        return validateEmail(value);
      case "password":
        return validatePassword(value);
      default:
        return "";
    }
  };

  const handleBlur = (e) => {
    const { id } = e.target;
    setTouched((prev) => ({
      ...prev,
      [id]: true,
    }));

    const error = validateField(id, formData[id]);
    setErrors((prev) => ({
      ...prev,
      [id]: error,
    }));
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));

    if (touched[id]) {
      const error = validateField(id, value);
      setErrors((prev) => ({
        ...prev,
        [id]: error,
        general: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      email: true,
      password: true,
    });

    const newErrors = {
      email: validateEmail(formData.email),
      password: validatePassword(formData.password),
      general: "",
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    setIsSubmitting(true);

    const loginCredential = {
      email: formData.email.trim(),
      password: formData.password,
    };

    loginMutation.mutate(loginCredential, {
      onSuccess: (response) => {
        toast.success("Login Successful")
        console.log(response)
        dispatch(setCredentials(response));
        navigate("/dashboard");
        setIsSubmitting(false)
      },
      onError: (error) => {
        setErrors((prev) => ({
          ...prev,
          general:
            error.response?.data?.message ||
            "Invalid email or password. Please try again.",
        }));
        setIsSubmitting(false);
      },
    });

  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-[#CCF6E5] px-4 md:px-0">
      <div className="flex justify-center mb-6">
        <img src={logo} alt="Novacrust Logo" className="m-16 h-10 md:h-12" />
      </div>
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-md rounded-2xl px-4 md:px-8 pt-4 md:pt-6 pb-8 mb-4 w-full md:w-[30rem] flex flex-col"
      >
        <div className="flex flex-col items-start py-6 mb-2">
          <h2 className="text-3xl font-semibold">Welcome</h2>
          <p className="text-[#4F4F4F] text-base">
            Sign in to your Novacrust admin account
          </p>
        </div>

        {errors.general && (
          <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">
            {errors.general}
          </div>
        )}

        <div className="mb-4">
          <input
            className={`shadow appearance-none border rounded-3xl w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
              errors.email && touched.email
                ? "border-red-500"
                : "border-[#CCF6E5]"
            }`}
            id="email"
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "email-error" : undefined}
            disabled={isSubmitting}
          />
          {errors.email && touched.email && (
            <p id="email-error" className="mt-1 text-xs text-red-500">
              {errors.email}
            </p>
          )}
        </div>

        <div className="flex flex-col mb-6">
          <div className="relative">
            <input
              className={`shadow appearance-none border rounded-3xl w-full py-3 px-3 pr-10 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                errors.password && touched.password
                  ? "border-red-500"
                  : "border-[#CCF6E5]"
              }`}
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? "password-error" : undefined}
              disabled={isSubmitting}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="flex absolute inset-y-0 right-0 items-center pr-3 text-gray-400 hover:text-gray-600"
              disabled={isSubmitting}
            >
              <svg
                className="h-6 w-6 text-[#013941]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    showPassword
                      ? "M3 3l18 18M10.5 10.677a2 2 0 002.823 2.823"
                      : "M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  }
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    showPassword
                      ? "M17.25 17.25L21 21M14.12 14.12a2 2 0 01-2.24 0"
                      : "M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  }
                />
              </svg>
            </button>
          </div>
          {errors.password && touched.password && (
            <p id="password-error" className="mt-1 text-xs text-red-500">
              {errors.password}
            </p>
          )}
          <button
            type="button"
            onClick={handleForgotPassword}
            className="font-serif text-[#013941] font-semibold self-end py-3 text-sm hover:underline"
          >
            Forgot password?
          </button>
        </div>

        <div className="flex justify-between items-center self-center">
          <button
            className={`bg-[#013941] text-white font-sans py-3 px-20 rounded-3xl focus:outline-none focus:shadow-outline transition-colors ${
              isSubmitting
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-[#024955]"
            }`}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Signing In..." : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Login;
